import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class PortalApiService {
	buildVersion = '';

	// Bearer token for a staff preview, set by PortalPreviewService once the handshake completes.
	// Sent as a header rather than held in a cookie so previews of different projects can be open
	// side by side, and so nothing is left behind in the browser afterwards.
	previewToken: string | null = null;

	preview = {
		start: (data, done, fail = null) => this.post('portal_preview_start', data, done, fail),
		end: (done = null, fail = null) => this.post('portal_preview_end', {}, done, fail)
	};

	auth = {
		branding: (done, fail = null) => this.get('portal_branding', done, fail),
		session: (done, fail = null) => this.get('portal_session', done, fail),
		logout: (done, fail = null) => this.get('portal_logout', done, fail),
		login: (data, done, fail = null) => this.post('portal_login', data, done, fail),
		inviteDetails: (token, done, fail = null) => this.get('portal_invite_details?token=' + encodeURIComponent(token || ''), done, fail),
		acceptInvite: (data, done, fail = null) => this.post('portal_accept_invite', data, done, fail),
		requestReset: (data, done, fail = null) => this.post('portal_request_reset', data, done, fail),
		resetDetails: (token, done, fail = null) => this.get('portal_reset_details?token=' + encodeURIComponent(token || ''), done, fail),
		resetPassword: (data, done, fail = null) => this.post('portal_reset_password', data, done, fail),
		updateProfile: (data, done, fail = null) => this.post('portal_update_profile', data, done, fail),
		dialCodes: (done, fail = null) => this.get('portal_dial_codes', done, fail)
	};

	project = {
		list: (done, fail = null) => this.get('portal_projects', done, fail),
		get: (id, done, fail = null) => this.get('portal_project?id=' + id, done, fail),
		actions: (id, done, fail = null) => this.get('portal_project_actions?id=' + id, done, fail),
		totals: (id, done, fail = null) => this.get('portal_project_totals?id=' + id, done, fail),
		footer: (id, done, fail = null) => this.get('portal_project_footer?id=' + id, done, fail),
		quotes: (id, done, fail = null) => this.get('portal_project_quotes?id=' + id, done, fail),
		changeOrders: (id, done, fail = null) => this.get('portal_project_change_orders?id=' + id, done, fail),
		changeOrderChanges: (id, done, fail = null) => this.get('portal_change_order_changes?id=' + id, done, fail),
		salesOrders: (id, done, fail = null) => this.get('portal_project_sales_orders?id=' + id, done, fail),
		invoices: (id, done, fail = null) => this.get('portal_project_invoices?id=' + id, done, fail),
		progress: (id, done, fail = null) => this.get('portal_project_progress?id=' + id, done, fail),
		documents: (id, done, fail = null) => this.get('portal_project_documents?id=' + id, done, fail),
		notifications: (done, fail = null) => this.get('portal_notifications', done, fail),
		activity: (done, fail = null) => this.get('portal_activity', done, fail)
	};

	quote = {
		markToConfirm: (id, data = {}, done = null, fail = null) => this.post('portal_mark_quote_to_confirm?id=' + id, data, done, fail),
		reject: (id, data = {}, done = null, fail = null) => this.post('portal_reject_quote?id=' + id, data, done, fail),
		document: (id, target, mode, done = null, fail = null) =>
			this.get('portal_document?id=' + id + '&target=' + target + '&mode=' + mode, done, fail)
	};

	private readonly base = '/api/portal_public/';

	constructor(private http: HttpClient) {}

	private get(func, done = null, fail = null) {
		this.http.get(this.base + func, this.options()).subscribe(response => {
			this.captureBuild(response.headers);
			done?.(response.body);
		}, error => {
			this.captureBuild(error?.headers);
			fail?.(this.getJsonError(error));
		});
	}

	private post(func, data = {}, done = null, fail = null) {
		this.http.post(this.base + func, data, this.options()).subscribe(response => {
			this.captureBuild(response.headers);
			done?.(response.body);
		}, error => {
			this.captureBuild(error?.headers);
			fail?.(this.getJsonError(error));
		});
	}

	// Every call goes through here, so a preview only has to be authenticated in one place.
	// `observe` has to stay a literal type or HttpClient resolves to its ArrayBuffer overload.
	private options() {
		return {
			withCredentials: true,
			observe: 'response' as const,
			headers: this.previewToken ? { 'X-Portal-Preview': this.previewToken } : undefined
		};
	}

	private captureBuild(headers): void {
		const build = headers?.get?.('X-Build');
		if (build) this.buildVersion = build;
	}

	private getJsonError(error): any {
		const status = error?.status ?? 0;
		try {
			const body = error?.error;
			if (body && typeof body === 'object' && !(body instanceof ErrorEvent) && body.message) {
				return { ...body, status };
			}
			if (body instanceof ErrorEvent) return { message: body.message, status };
			return { message: status ? 'Internal server error.' : 'Connection error.', status };
		} catch (ex) {
			return { message: 'Internal server error.', status };
		}
	}
}
